import { Ticker } from "pixi.js"
import Player from "./Player"
import OverlayText from "./OverlayText"
import Level from "./Level"
import { playerIcons } from "src/common/Player"
import Clock, { ClockCallbackObject } from "./Clock"
import { PlayerIdBool } from "$types"

export type GameOverCallback = (level: Match, winner: Player | null) => void
export type StartCallback = (level: Match) => void
export type ExitCallback = (level: Match) => void
export type ClockTickCallback = (level: Match, secondsLeft: number) => void

export default class Match extends Level {
  private _gameOver: boolean = false
  get gameOver() {
    return this._gameOver
  }

  private _started = false
  get started() {
    return this._started
  }

  onGameOver?: GameOverCallback
  onStart?: StartCallback
  onClockTick?: ClockTickCallback
  onExit?: ExitCallback

  private _gameInterval: ClockCallbackObject | null = null

  private _matchNumber: number
  private _closingTime: number
  private _secondsLeft: number

  private _startAt: string = new Date().toISOString()
  get startAt() {
    return this._startAt
  }

  private _endAt: string | null = null
  get endAt() {
    return this._endAt
  }

  get secondsLeft() {
    return this._secondsLeft
  }
  set secondsLeft(value) {
    if (value < 0) {
      value = 0
    }
    this._secondsLeft = value
  }

  private _hurryUpText: OverlayText | null = null

  constructor(params: {
    x: number,
    y: number,
    levelData: string,
    onStart?: StartCallback,
    onGameOver?: GameOverCallback,
    onClockTick?: ClockTickCallback,
    onExit?: ExitCallback,
    closingTime: number,
    secondsLeft: number,
    matchNumber: number,
    activePlayers: PlayerIdBool,
    connectedPlayers: PlayerIdBool,
  }) {
    super({
      x: params.x,
      y: params.y,
      levelData: params.levelData,
      activePlayers: params.activePlayers,
      connectedPlayers: params.connectedPlayers
    })

    this.onStart = params.onStart
    this.onGameOver = params.onGameOver
    this.onClockTick = params.onClockTick
    this.onExit = params.onExit

    this._closingTime = params.closingTime
    this._secondsLeft = params.secondsLeft
    this._matchNumber = params.matchNumber
  }

  onFadeInComplete(): void {
    super.onFadeInComplete()
    new OverlayText({
      text: `Match ${this._matchNumber}`,
      duration: 1000,
      container: this,
      type: "centered",
    }).onRemove = () => {
      this.start()
    }
  }

  tickUpdate(ticker: Ticker) {
    super.tickUpdate(ticker)

    if (!this._gameOver && Object.values(this.players).reduce((acc, player) => acc + (player ? player.alive ? 1 : 0 : 0), 0) <= 1) {
      this.setGameOver()
    }

    if (!this._gameOver && this._secondsLeft <= this._closingTime && this._closingIterations === 0) {
      this._closeLevelTiles(0, Level.CLOSING_SPIRALS)
      this._hurryUpText = new OverlayText({
        text: "Hurry up!",
        container: this,
        color: 0xfe2000,
        type: "scrolling",
      })
    }
  }

  start() {
    this._started = true
    if (this.onStart) this.onStart(this)

    this._gameInterval = Clock.setInterval(() => {
      this.secondsLeft--
      if (this.onClockTick) this.onClockTick(this, this.secondsLeft)
      if (this.secondsLeft === 0) {
        this.setGameOver()
      }
    }, 1000)

  }

  setGameOver() {
    this._gameOver = true
    this._started = false
    this.stopClosing()

    this._endAt = new Date().toISOString()

    if (this._gameInterval) Clock.clear(this._gameInterval)
    if (this._hurryUpText) this._hurryUpText.remove()

    const winner = this.getWinner()
    let text
    if (winner) {
      winner.startBlink(0xffffff)
      text = `${playerIcons[winner.playerId].toUpperCase()} player wins!`
    } else {
      text = "Draw!"
    }
    if (this.onGameOver) this.onGameOver(this, winner)

    new OverlayText({
      text,
      duration: 3000,
      container: this,
      type: "centered",
    }).onRemove = () => {
      this.fadeOut()
    }
  }

  onFadeOutComplete(): void {
    if (this.onExit) this.onExit(this)
  }

  getWinner(): Player | null {
    if (!this._gameOver) return null
    const activePlayers = Object.values(this.players).filter(p => p && p.alive)
    return activePlayers.length === 1 ? activePlayers[0] : null
  }
}
